import React, { FC, useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { GIButton } from 'app-components';
import { useDisclosure, useAppSelector, useAppDispatch } from '../../hooks/index';
import { ConditionalRendering } from '../../components/common/conditional-rendering';
import { ValidationErrorMessage } from '../../components/common/form';
import { IS_DEFAULT_WORKGROUP, RESPONSE_STATUS } from '../../constants/ticketMgmt/manageWorkgroup.constants';
import { WorkGroupModal } from '../../containers/ticketMgmtPortal/WorkgroupModal';
import ManageWorkgroupTable from './ManageWorkgroupTable';
import SearchWorkgroup from './SearchWorkgroup';
import useManageWorkgroup from './hooks/useManageWorkgroup';
import {
    submitWorkgroupListChanges,
    fetchManageWorkgroupDetails,
    getAllTags,
} from '../../actions/ticketMgmtPortal/manageWorkgroupActionCreator';

const Title = styled.h2`
    margin: 0;
    margin-bottom: 2rem;
`;

const StyledContainer = styled.div`
    .item-center {
        text-align: center;
    }

    .bg-color {
        background-color: ${({ theme }) => theme.dangerLightest};
    }
`;

const StyledSuccessMessage = styled.div`
    margin: 4rem;
    text-align: center;
    color: ${({ theme }) => theme.successMain};
    font-weight: bold;
`;

const StyledControls = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-top: 2rem;
`;

const ManageWorkgroupContainer: FC = () => {
    const dispatch: Dispatch<any> = useAppDispatch();
    const { isOpen, onOpen, onClose: onWorkgroupModalClose } = useDisclosure();
    const { getQueueIds } = useManageWorkgroup();
    const { workGroupList, submitWorkgroupListMsg } = useAppSelector(state => state.manageWorkGroup);

    const [defaultWorkgroups, setDefaultWorkgroups] = useState<Record<number, boolean>>(() =>
        workGroupList.reduce(
            (acc, wg) => {
                acc[wg.id] = wg.defaultWorkGroup === IS_DEFAULT_WORKGROUP.YES;
                return acc;
            },
            {} as Record<number, boolean>,
        ),
    );

    useEffect(() => {
        dispatch(fetchManageWorkgroupDetails());
        dispatch(getAllTags());
    }, []);

    useEffect(() => {
        setDefaultWorkgroups(
            workGroupList.reduce(
                (acc, wg) => {
                    acc[wg.id] = wg.defaultWorkGroup === IS_DEFAULT_WORKGROUP.YES;
                    return acc;
                },
                {} as Record<number, boolean>,
            ),
        );
    }, [workGroupList]);

    const handleToggleDefault = useCallback((id: number) => {
        setDefaultWorkgroups(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    }, []);

    const hasWorkgroupListChanged = useCallback(() => {
        return workGroupList.some(wg => {
            const currentIsDefault = defaultWorkgroups[wg.id];
            const originalIsDefault = wg.defaultWorkGroup === IS_DEFAULT_WORKGROUP.YES;
            return currentIsDefault !== originalIsDefault;
        });
    }, [workGroupList, defaultWorkgroups]);

    const handleReset = () => {
        setDefaultWorkgroups(
            workGroupList.reduce(
                (acc, wg) => {
                    acc[wg.id] = wg.defaultWorkGroup === IS_DEFAULT_WORKGROUP.YES;
                    return acc;
                },
                {} as Record<number, boolean>,
            ),
        );
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const updatedWorkgroups = workGroupList.map(wg => ({
            ...wg,
            defaultWorkGroup: defaultWorkgroups[wg.id] ? IS_DEFAULT_WORKGROUP.YES : IS_DEFAULT_WORKGROUP.NO,
        }));

        const queueIds = getQueueIds(updatedWorkgroups);
        dispatch(submitWorkgroupListChanges(queueIds));
    };

    const onClose = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        onWorkgroupModalClose();
    };

    const handleModalOpen = () => {
        onOpen();
    };

    return (
        <>
            <StyledContainer className="gi-panel" data-testid='manage-workgroup-form-container'>
                <Title className="bg-color-padding" data-testid='manage-workgroup-header'>
                    <FormattedMessage id="ticket_management.table.workgroup.form.workgroup.search.header"/>
                </Title>
                <SearchWorkgroup />

                <Title className="bg-color-padding" data-testid='manage-workgroup-subTitle'>
                    <FormattedMessage id="ticket_management.table.workgroup.label.df_subTitle" />
                </Title>

                <span className="p-x-1r data-testid='manage-workgroup-add-btn-span'">
                    <GIButton
                        id="addWorkgroupBtn"
                        dataTestId="add_workgroup_btn"
                        labelId="ticket_management.manage_workgroup.add_workgroup"
                        onClickHandler={handleModalOpen}
                        isDisabled={false}
                    />
                </span>
                <form className="p-x-1r" onSubmit={handleSubmit} onReset={handleReset} data-testid='manage-workgroup-form'>
                    <ManageWorkgroupTable
                        workgroups={workGroupList}
                        defaultWorkgroups={defaultWorkgroups}
                        onToggleDefault={handleToggleDefault}
                    />

                    <ValidationErrorMessage
                        errorWrapperClasses={['item-center']}
                        errorClasses={['bg-color']}
                        isVisible={submitWorkgroupListMsg.responseStatus === RESPONSE_STATUS.FAILURE}
                        messageText={submitWorkgroupListMsg.message}
                    />

                    <ConditionalRendering isTrue={submitWorkgroupListMsg.responseStatus === RESPONSE_STATUS.SUCCESS}>
                        <StyledSuccessMessage data-testid='manage-workgroup-save-success'>
                            <FormattedMessage id="ticket_management.manage_workgroup.table.workgroup.form.save.success" />
                        </StyledSuccessMessage>
                    </ConditionalRendering>

                    <StyledControls data-testid='manage-workgroup-controls'>
                        <GIButton
                            id="manage_workgroup_button_clear"
                            dataTestId="manage_workgroup_button_clear"
                            variant="secondary"
                            labelId="button.reset"
                            type="reset"
                        />
                        <GIButton
                            id="manage_workgroup_button_search"
                            dataTestId="manage_workgroup_button_search"
                            variant="primary"
                            labelId="button.submit"
                            type="submit"
                            isDisabled={!hasWorkgroupListChanged()}
                        />
                    </StyledControls>
                </form>
            </StyledContainer>
            <ConditionalRendering isTrue={isOpen}>
                <WorkGroupModal onClose={onClose} />
            </ConditionalRendering>
        </>
    );
};

export default ManageWorkgroupContainer;